<template>
  <span>
    <span v-if="!isMe || !$store.state.edit.on">
      <!--TODO: Might need to look into potential leakage of placeholder into production view when it is not supposed to-->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span v-html="empty ? placeholder : editableText" />
    </span>
    <span v-else @click="edit" @mouseover="hover = true" @mouseout="hover = false">
      <span v-show="!editing" style="position: relative">
        <span
          v-if="offsetIconsToLeft"
          :class="{ 'offset-for-large-text': largeTextSize }"
          class="loader-wrapper-left"
          style=""
        >
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <NetworkLoader :loader="loading" :failed="failed" />
        </span>
        <i
          v-if="offsetIconsToLeft"
          v-show="hover && !loading"
          :class="{
            'offset-to-left': offsetIconsToLeft,
            'offset-to-left-for-large-text': largeTextSize,
          }"
          class="zmdi zmdi-edit c-gray left-text"
        />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span ref="display" class="iblock" v-html="empty ? placeholder : editableText" />
        <i
          v-if="!offsetIconsToLeft"
          v-show="hover && !loading"
          :class="{ 'offset-to-right': !offsetIconsToLeft }"
          class="zmdi zmdi-edit c-gray right-text"
        />
        <span
          v-if="!offsetIconsToLeft"
          :class="{ 'offset-for-large-text': largeTextSize }"
          class="loader-wrapper-right"
          style=""
        >
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <NetworkLoader :loader="loading" :failed="failed" />
        </span>
      </span>
      <span v-show="editing">
        <span v-if="!textarea" style="position: relative; z-index: 4; display: inline-block">
          <input
            ref="input"
            v-model="editableText"
            v-autowidth="{ maxWidth: '100%', minWidth: '20px', comfortZone: 0 }"
            :placeholder="placeholder"
            :type="numbersOnly ? 'number' : 'text'"
            class="form-control f-18"
            @focusout="editing = false"
            @keyup.enter="editing = false"
          />
        </span>
        <div v-else style="position: relative; z-index: 4">
          <textarea
            ref="input"
            v-model="editableText"
            :style="{ height: inputHeight + 'px' }"
            :placeholder="placeholder"
            class="form-control ncase f-14"
            @focusout="handleFocusOut"
          />
        </div>
      </span>
    </span>
  </span>
</template>

<script>
import autosize from 'autosize'
import { nextTick } from 'vue'
import enums from '@/utils/enum'
import { SET_EDIT_UNSAVED, SET_EDITABLE_ACTIVE_STATE } from '@/state/mutation-types'
import { SET_EDIT_SAVING_STATE } from '@/state/action-types'
import { UPDATE_ACCOUNT, UPDATE_COMPANY, UPDATE_FREELANCER } from '@/state/apollo/accounts'
import { NetworkError } from '@/utils/notification'

function decodeHtml(html) {
  const txt = document.createElement('textarea')
  txt.innerHTML = html.replace(/\n\r?/g, '')
  return txt.value.replace(/<br \/>\r?/g, '\n')
}

export default {
  name: 'ComponentEditText',
  props: {
    isMe: {
      type: Boolean,
      required: true,
    },
    fieldName: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    numbersOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    textarea: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: $t('Add text here'),
    },
    revertOnFail: {
      type: Boolean,
      required: false,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    offsetIconsToLeft: {
      type: Boolean,
      default: false,
    },
    largeTextSize: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editableText: '',
      viewAbleText: '',
      editing: false,
      loading: false,
      hover: false,
      failed: false,
      inputHeight: 0,
    }
  },
  computed: {
    empty: {
      get() {
        return this.editableText === '' || this.editableText === null
      },
    },
    addHTMLSpaces: {
      get() {
        return this.empty ? this.placeholder : this.editableText
      },
    },
  },
  watch: {
    loading(saving) {
      this.$store.dispatch(SET_EDIT_SAVING_STATE, saving)
    },
    editing() {
      this.$store.commit(SET_EDIT_UNSAVED, true)
      if (!this.editing) {
        this.$store.commit(SET_EDITABLE_ACTIVE_STATE, false)
        this.saveChanges()
      }
    },
  },
  created() {
    this.editableText = this.text
    this.viewAbleText = this.editableText
  },
  methods: {
    edit() {
      this.updateInputHeight()
      // This is to prevent a bug where the edit function more than once is executed because
      // the user clicks on the textarea his will cause the text they are editing to get all
      // of their line breaks removed.

      if (!this.editing) {
        this.editableText = decodeHtml(this.editableText)
      }
      this.editing = true
      this.failed = false
      this.$store.commit(SET_EDITABLE_ACTIVE_STATE, true)
      nextTick(() => {
        this.$refs.input.focus()
        if (typeof autosize === 'function') {
          autosize(this.$refs.input)
        }
      })
    },
    updateInputHeight() {
      if (!this.editing && this.isMe) {
        this.inputHeight = this.$refs.display.offsetHeight
      }
    },
    async saveChanges() {
      if (this.viewAbleText === this.editableText) {
        return // No need to update as no changes has occurred
      }
      this.loading = true
      try {
        const data = {
          globalId: this.account.globalId,
          [this.fieldName]: decodeHtml(this.editableText),
        }

        const mutation = this.account.isFreelancer
          ? UPDATE_FREELANCER
          : this.account.isCompany
          ? UPDATE_COMPANY
          : UPDATE_ACCOUNT

        await this.$apollo.mutate({
          mutation,
          variables: {
            input: data,
          },
        })

        if (this.editableText !== '') {
          this.viewAbleText = this.editableText
        }
        this.$store.commit(SET_EDIT_UNSAVED, false)
      } catch (e) {
        this.$store.commit(SET_EDIT_UNSAVED, true)
        new NetworkError({
          severity: enums.notification.severity.CRITICAL,
          ex: e,
        }).dispatch(this)
        this.failed = true
        if (this.revertOnFail) {
          this.editableText = this.viewAbleText
        }
      }
      this.loading = false
    },
    handleFocusOut() {
      this.editing = false
      this.editableText = this.editableText.replace(/\n\r?/g, '<br />')
    },
  },
}
</script>

<style scoped lang="scss">
.zmdi {
  top: 5px;
}
i {
  position: absolute;
  margin-left: 5px;
}
.right-text {
  right: 0;
}
.bar-pos {
  top: 100%;
  left: 0px;
  width: 100%;
}
input {
  margin: 0 auto;
  float: none;
}
textarea {
  width: 100% !important;
  border: none;
}
.hidden-spacer {
  max-height: 0px;
  visibility: hidden;
  position: absolute;
}
.offset-to-right {
  margin-right: -40px;
}
.offset-to-left {
  margin-left: -32px;
}
.offset-to-left-for-large-text {
  margin-left: -40px;
}
.loader-wrapper-left {
  position: absolute;
  left: -50px;
  top: -15px;
}
.loader-wrapper-right {
  position: absolute;
  right: -55px;
  top: -15px;
}
.offset-for-large-text {
  top: -5px !important;
}
</style>
